import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Auto-load-more: presses a list's own Load more when its paging row scrolls near the viewport.
 *
 * The returned ref goes on the paging container — the row that already holds the button — and the
 * button **stays**. Three reasons, each one a way the observer alone fails somebody: a keyboard or
 * switch user gets a control instead of having to scroll-to-trigger; the end of the list is still a
 * visible sentence rather than a spinner that quietly stopped; and an observer that never fires (a
 * display-none ancestor, a UA without the API) degrades to exactly the page we had before.
 *
 * Re-arming is the one subtle part. An observer only reports *crossings*, so a load that finishes with
 * the row still in view would never fire again — which is why the effect re-runs on `active`: the
 * caller passes `hasMore && !loadingMore`, every completed load flips it false→true, and a fresh
 * `observe()` reports the current intersection immediately. Short pages chain-load until the row
 * finally leaves the margin, tall ones until the list ends.
 *
 * `rootMargin` starts the fetch ~one viewport early, so the reader scrolls into rows that are already
 * there rather than into the button. A caller can narrow it (`margin`) where eager is wrong — the
 * comment thread loads only when the reader is close to its end, because comments are read
 * top to bottom and pre-fetching pages nobody reached just grows the page under the rail.
 */
export function useAutoLoadMore(
  active: boolean,
  onLoad: () => void,
  { margin = '600px 0px' }: { readonly margin?: string } = {},
): (element: Element | null) => void {
  const [element, setElement] = useState<Element | null>(null);

  // The latest callback without re-observing per render — the observer's lifetime is tied to the
  // element and to `active`, not to the identity of a closure the caller rebuilds every render.
  // Written in an effect (react-hooks/refs): effects run before any observer callback can.
  const loadRef = useRef(onLoad);
  useEffect(() => {
    loadRef.current = onLoad;
  });

  useEffect(() => {
    if (!active || element === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadRef.current();
      },
      { rootMargin: margin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [active, element, margin]);

  return useCallback((node: Element | null) => setElement(node), []);
}
